import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { handleAuthCallback, syncMicrosoftAuthToLocal } from '../../models/authModel';
import { verifySession, verifySessionWithLiveServer, getAndClearRedirectPath } from '../../services/authService'; // Import both verification functions

function AuthCallback() {
  const { isAuthenticated, loading, updateUser } = useAuth();
  const [processedAuth, setProcessedAuth] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const processAuth = async () => {
      try {
 
        const urlParams = new URLSearchParams(window.location.search);
 
        // Check if we have the required parameters
        if (!urlParams.get('token') || !urlParams.get('user')) {
          console.log('No authentication parameters found, skipping callback processing');
          setAuthError('No authentication data received');
          return;
        }
        
        // Process the authentication callback
        const authResult = handleAuthCallback();
        
        // Check for stored redirect path
        const storedRedirectPath = authResult.redirectPath || getAndClearRedirectPath();
        if (storedRedirectPath) {
          console.log('🔄 Found stored redirect path:', storedRedirectPath);
          setRedirectPath(storedRedirectPath);
        }
        
        if (authResult && authResult.authMethod === 'microsoft') { 
          
          try {
            // Sync Microsoft authentication to local server
            const localAuthResult = await syncMicrosoftAuthToLocal();
            
            if (localAuthResult && localAuthResult.user) {
              updateUser(localAuthResult.user);
              localStorage.setItem('user', JSON.stringify(localAuthResult.user));
              setProcessedAuth(true);
            } else {
              throw new Error('Local authentication sync failed');
            }
          } catch (syncError) {
            console.error('Failed to sync Microsoft auth to local server:', syncError);
            // Fallback to Microsoft user data but warn about potential issues
            updateUser(authResult.user);
            console.warn('Using Microsoft user data as fallback - local API calls may fail');
            setProcessedAuth(true);
          }
        } else if (authResult) {
          console.log('Regular authentication successful (callback):', authResult);
          updateUser(authResult.user);
          setProcessedAuth(true);
        } else {
          console.error('No authentication data found in callback');
          setAuthError('No authentication data received');
        }
      } catch (error) {
        console.error('Error processing auth callback:', error);
        setAuthError('Failed to process authentication data');
      }
    };

    if (!processedAuth && !isAuthenticated && window.location.search) {
      processAuth();
    }
  }, [processedAuth, isAuthenticated, updateUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Processing authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to dashboard or stored path if authenticated
  if (isAuthenticated || processedAuth) {
    const targetPath = redirectPath || '/dashboard';
    console.log('🔄 Redirecting to:', targetPath);
    return <Navigate to={targetPath} replace />;
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Authentication Error</h2>
          <p className="text-red-600 mb-4">{authError}</p>
          <a 
            href="/login" 
            className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            Return to login
          </a>
        </div>
      </div>
    );
  }

  // Default loading state
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>Loading authentication data...</p>
      </div>
    </div>
  );
}

export default AuthCallback;

